import { type BTFileSystem } from "legacyComponents/FileUploadContainer.types";
import { useCallback } from "react";
import { useHistory, useRouteMatch } from "react-router";

import { useWebviewContext } from "helpers/webview/WebviewContext";

import { canViewBTFileSystemInLightbox } from "commonComponents/btWrappers/BTLightbox/BTLightbox.utility";
import { usePanelAwareNavigate } from "commonComponents/btWrappers/BTSplitView/usePanelAwareNavigate";
import { showDownload } from "commonComponents/entity/media/common/MediaContextMenu/MediaContextMenu.utility";

import {
    buildAttachedMediaPropertiesRouting,
    canClickFile,
    downloadFromUrl,
    isPDFFileType,
} from "entity/media/common/media.utils";
import { useSharedContent } from "entity/sharedContent/useSharedContent";

interface IUseAttachedFileClickHandlerParams {
    /** Entity name segment used in the internal /Attachment/{entityName}/{entityId}/... route. */
    entityName: string;
    entityId: number;
    /**
     * Optional lightbox opener. When provided, lightbox-eligible files (images, supported video) call this
     * instead of routing or download. Pass undefined to skip lightbox handling for that caller.
     */
    onOpenLightbox?: (fileId: number) => void;
    /** Return true to suppress the default click handling (lightbox/route/download). */
    onFileClick?: (file: BTFileSystem) => boolean;
}

/**
 * Shared file-click handler for attached-media surfaces (Documents tables, workflow progress cards, etc.).
 * Centralizes the external vs internal branching so external (share-token) users always get a working
 * file action and the two call sites cannot drift.
 *
 * Share-token mode is detected from `SharedContentContext` and webview mode from `WebviewContext` —
 * callers do not need to pass those.
 */
export function useAttachedFileClickHandler({
    entityName,
    entityId,
    onOpenLightbox,
    onFileClick,
}: IUseAttachedFileClickHandlerParams): (file: BTFileSystem) => void {
    const history = useHistory();
    const match = useRouteMatch();
    const { shareToken } = useSharedContent();
    const isExternalView = shareToken !== undefined;
    const isWebview = useWebviewContext() !== undefined;
    const { querySuffix } = usePanelAwareNavigate();

    return useCallback(
        (file: BTFileSystem) => {
            if (onFileClick?.(file)) {
                return;
            }

            if (!canClickFile(file, entityId)) {
                return;
            }

            if (onOpenLightbox && canViewBTFileSystemInLightbox(file)) {
                onOpenLightbox(file.id);
                return;
            }

            if (isExternalView) {
                if (isPDFFileType(file.extension)) {
                    window.open(file.docPath, "_blank");
                } else if (showDownload(file, isExternalView, isWebview)) {
                    downloadFromUrl(file.downloadDocPath, file.title);
                }
                return;
            }

            const link = buildAttachedMediaPropertiesRouting(
                { fileId: file.id, mediaType: file.mediaType, jobId: file.jobsiteId },
                entityName,
                entityId,
            );
            history.push(match.url + link + querySuffix);
        },
        [
            entityId,
            entityName,
            history,
            match.url,
            onOpenLightbox,
            isExternalView,
            isWebview,
            querySuffix,
            onFileClick,
        ],
    );
}
